import { useMemo, useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import paths from "@icarius-router/paths";
import { getUserRole } from "@icarius-app/selectors";
import { getQueryGroupsAction, getQueriesAction, geminiChatAction } from "../actions";
import {
    getIsLoading,
    getQueryGroups,
    getQueries,
    getGroupsToFilter,
    getGeminiChatResult,
    getIsLoadingBot,
} from "../selectors";
import useIsPathAvailable from "@icarius-utils/hooks/useIsPathAvailable";

const useQueries = () => {
    const [gridIsOpen, setGridIsOpen] = useState(false);
    const [group, setGroup] = useState("");
    const [groupName, setGroupName] = useState("");
    const [queryPreset, setQueryPreset] = useState("");
    const [userPreset, setUserPreset] = useState("");

    const dispatch = useDispatch();
    const location = useLocation();
    const history = useHistory();

    const isLoading = useSelector(getIsLoading);
    const queryGroups = useSelector(getQueryGroups);
    const groupsToFilter = useSelector(getGroupsToFilter);
    const queries = useSelector(getQueries);
    const userRole = useSelector(getUserRole);
    const geminiChatResult = useSelector(getGeminiChatResult);
    const isLoadingBot = useSelector(getIsLoadingBot);
    const isAiQueriesEnabled = useIsPathAvailable(paths.aiQueryChatButton);

    useEffect(() => {
        dispatch(getQueryGroupsAction());
    }, [dispatch]);

    const handleOpenGrid = useCallback(
        (newGroup, newGroupName) => {
            setGridIsOpen(true);
            setGroup(newGroup);
            setGroupName(newGroupName || newGroup);
            dispatch(getQueriesAction(newGroup));
        },
        [dispatch]
    );

    const handleCloseGrid = () => {
        setGridIsOpen(false);
        setGroup("");
    };

    const handleSendMessage = async (data) => {
        return dispatch(geminiChatAction(data));
    };

    useEffect(() => {
        if (location.search) {
            const groupFromParams = new URLSearchParams(location.search).get("group");
            const groupNameFromParams = new URLSearchParams(location.search).get("groupName"); // para los de userMenu, donde el groupName es distinto al group
            const queryFromParams = new URLSearchParams(location.search).get("queryCode");
            const userFromParams = new URLSearchParams(location.search).get("employeeCode");

            setUserPreset(userFromParams);
            setQueryPreset(queryFromParams);
            handleOpenGrid(groupFromParams, groupNameFromParams);

            history.replace({
                pathname: userRole !== "E" ? paths.myQueries : paths.queries,
                search: "",
            });
        }
    }, [location.search, history, handleOpenGrid, userRole]);

    const filteredGroups = useMemo(() => {
        return queryGroups.filter((group) => !groupsToFilter.includes(group.toLowerCase()));
    }, [queryGroups, groupsToFilter]);

    const isFromHiddenGroup = groupsToFilter.includes(group.toLowerCase());

    const state = {
        gridIsOpen,
        group,
        groupName,
        queryPreset,
        userPreset,
    };

    const handlers = {
        handleOpenGrid,
        handleCloseGrid,
        handleSendMessage,
    };

    return {
        groups: filteredGroups,
        state,
        handlers,
        isLoading,
        isLoadingBot,
        isFromHiddenGroup,
        queries,
        geminiChatResult,
        isAiQueriesEnabled,
    };
};

export default useQueries;
